import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'
import { Input } from '@/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { RadioGroup, RadioGroupItem } from '@/ui/radio-group'
import { Label } from '@/ui/label'
import { useQueryClient } from '@tanstack/react-query'
import { useAppSelector } from '@/app/reduxHooks'
import { useCreateDataFlowScheduleMutation } from '@/features/adp/hooks/useCreateDataFlowScheduleMutation'
import { toast } from 'sonner'
import { useDispatch } from 'react-redux'
import { closeOverlay, type SchedulePipelineOverlayData } from '@/features/adp/state/overlaySlice'
import { setScheduleDetails } from '@/features/adp/state/pipelineSlice'
import { useRunJobMutation } from '../../mutations/usePipelineMutations'
import { useUpdateRecordMutation } from '../../mutations/useCommonMutations'
import { getTodayDateString, type SchedulePipelineFormValues, schedulePipelineSchema } from '../../schemas/SchedulePipeline'
import {
  buildFormValuesFromSchedule,
  buildScheduleCreatePayload,
  buildScheduleUpdatePayload,
  formValuesToScheduleDetails,
} from '../../utils/schedulePipeline.util'

const DATA_FLOW_SCHEDULER_TABLE = 'data_flow_schedular'

const SchedulePipeline = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const overlayState = useAppSelector(state => state.overlay)
  const overlayData = (overlayState.type === 'schedule-pipeline-modal' ? overlayState.data : null) as SchedulePipelineOverlayData | null
  const isOpen = overlayState.isOpen && overlayState.type === 'schedule-pipeline-modal'

  const defaultFormValues = useMemo(
    () =>
      buildFormValuesFromSchedule(
        overlayData?.isSchedule,
        overlayData?.scheduleDetails,
        overlayData?.scheduleTypeName
      ),
    [overlayData?.isSchedule, overlayData?.scheduleDetails, overlayData?.scheduleTypeName]
  )

  const { mutateAsync: createDataFlowSchedule, isPending: isCreatingSchedule } =
    useCreateDataFlowScheduleMutation()
  const { mutateAsync: updateScheduleRecord, isPending: isUpdatingSchedule } = useUpdateRecordMutation()
  const { mutateAsync: runJob, isPending: isRunningJob } = useRunJobMutation()

  const form = useForm<SchedulePipelineFormValues>({
    resolver: zodResolver(schedulePipelineSchema),
    mode: 'onChange',
    defaultValues: defaultFormValues,
  })

  const mode = form.watch('mode')
  const frequency = form.watch('frequency')
  const isEditMode = Boolean(overlayData?.scheduleDetails)
  const isSaveDisabled =
    !form.formState.isValid ||
    form.formState.isSubmitting ||
    isCreatingSchedule ||
    isUpdatingSchedule ||
    isRunningJob

  const hourlyValues = Array.from({ length: 24 }, (_, idx) => idx + 1)
  const minuteValues = Array.from({ length: 59 }, (_, idx) => idx + 1)

  const pipelineName = overlayData?.pipelineName ?? ''
  const sessionId = overlayData?.sessionId ?? ''
  const targetDataFlowId = overlayData?.dataFlowId ?? null

  useEffect(() => {
    if (isOpen) {
      form.reset(defaultFormValues)
    }
  }, [defaultFormValues, form, isOpen])

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.reset(defaultFormValues)
      dispatch(closeOverlay())
    }
  }

  const handleSubmit = async (values: SchedulePipelineFormValues) => {
    if (!values.startDate || !values.startTime) return

    if (!pipelineName?.trim()) {
      toast.error('Pipeline name is missing. Please try again.')
      return
    }

    if (!sessionId?.trim()) {
      toast.error('Session is missing. Please try again.')
      return
    }

    if (isEditMode) {
      if (targetDataFlowId == null) {
        toast.error('Pipeline id is missing. Cannot update schedule.')
        return
      }
      try {
        await updateScheduleRecord({
          tableName: DATA_FLOW_SCHEDULER_TABLE,
          payload: buildScheduleUpdatePayload(targetDataFlowId, values),
        })
      } catch {
        toast.error('Unable to update the schedule. Please try again.')
        return
      }
    } else {
      if (targetDataFlowId == null) {
        toast.error('Pipeline id is missing. Cannot create schedule.')
        return
      }
      const payload = buildScheduleCreatePayload(pipelineName, values)

      try {
        await createDataFlowSchedule({ dataFlowId: targetDataFlowId, payload })
      } catch {
        toast.error('Unable to schedule the pipeline right now. Please try again.')
        return
      }
    }

    try {
      await runJob(sessionId)
    } catch {
      toast.error('Schedule saved, but we could not trigger a run. Please try again.')
      return
    }

    const nextDetails = formValuesToScheduleDetails(values)
    if (overlayData?.persistScheduleInPipelineStore) {
      dispatch(setScheduleDetails(nextDetails))
    } else {
      void queryClient.invalidateQueries({ queryKey: ['pipeline-list'] })
    }

    toast.success(
      isEditMode ? 'Pipeline schedule updated successfully.' : 'Pipeline scheduled successfully.'
    )
    handleDialogOpenChange(false);
  }

  const dialogTitle = isEditMode ? 'Edit Pipeline Schedule' : 'Create Pipeline Schedule'
  const submitLabel = isEditMode ? 'Update' : 'Save'
  const dialogDescription = isEditMode ? 'Update when and how this pipeline should run.' : 'Configure when and how this pipeline should run.'

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleDialogOpenChange}
    >
      <DialogContent
        className="max-w-2xl overflow-hidden p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 px-6 py-5">
            <FormField
              control={form.control}
              name="mode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mode</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value)
                        if (value === 'onetime') {
                          form.setValue('frequency', 'daily')
                          form.setValue('frequencyhrs', 5)
                          form.clearErrors(['frequency', 'frequencyhrs'])
                        }
                      }}
                      className="flex items-center gap-6 pt-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="onetime" id="schedule-mode-onetime" />
                        <Label htmlFor="schedule-mode-onetime" className="text-base font-normal">
                          One Time
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="repeated" id="schedule-mode-repeated" />
                        <Label htmlFor="schedule-mode-repeated" className="text-base font-normal">
                          Repeated
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value ?? ''}
                        min={getTodayDateString()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <Input
                        type="time"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {mode === 'repeated' && (
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Frequency</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value: 'daily' | 'hourly' | 'weekly' | 'minute-wise') =>
                        field.onChange(value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="minute-wise">Minute-Wise</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {mode === 'repeated' && (frequency === 'hourly' || frequency === 'minute-wise') && (
              <FormField
                control={form.control}
                name="frequencyhrs"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Frequency {frequency === 'hourly' ? 'Hours' : 'Minutes'}
                    </FormLabel>
                    <Select
                      value={field.value ? String(field.value) : ''}
                      onValueChange={(value) => field.onChange(Number(value))}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              frequency === 'hourly'
                                ? 'Select frequency hours'
                                : 'Select frequency minutes'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(frequency === 'hourly' ? hourlyValues : minuteValues).map((value) => (
                          <SelectItem key={value} value={String(value)}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-3 border-t pt-5">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isSaveDisabled}>{submitLabel}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default SchedulePipeline
